import { SeoKey } from "./seoData";

// Pages under /:clusterId, keyed by their one path segment.
const CLUSTER_PAGES: Record<string, SeoKey> = {
  dashboard: "Dashboard",
  payments: "Payments",
  "subscriptions-plans": "SubscriptionAndPlans",
  documents: "Documents",
  "payment-methods": "PaymentMethods",
  "action-center": "ActionCenter",
  "my-profile": "UserProfile",
  notification: "Notification",
  "make-payment": "MakePayment",
  "support-help": "SupportHelp",
  "quick-checkout": "QuickCheckout",
  login: "Login",
  "login-via-cred": "LoginViaCred",
  "otp-send": "OtpSend",
  "otp-verification": "OtpVerification",
  "forgot-password": "ForgotPassword",
  "forgot-otp-verification": "ForgotOtpVerification",
  "reset-password": "ResetPassword",
  "sign-up": "SignUp",
  "sign-up-email-attached": "SignUpEmailAttached",
  "sign-up-otp-verification": "SignUpOtpVerification",
  "sign-up-password": "SignUpPassword",
  "set-password": "SetPassword",
  "request-access": "RequestAccess",
  "access-request-send-successfully": "AccessRequestSent",
};

// Link pages that carry no cluster id, keyed by their first segment, with the
// segment counts App.tsx routes them at. Checked before the cluster pages
// because the router ranks these static segments above /:clusterId.
const LINK_PAGES: Record<string, { key: SeoKey; segments: number[] }> = {
  agreement: { key: "Agreement", segments: [2] },
  payment_link: { key: "PaymentLink", segments: [2] },
  invoice: { key: "Invoice", segments: [2] },
  quotes: { key: "Quotes", segments: [2, 3] },
  product: { key: "Products", segments: [2] },
  "view-payment": { key: "ViewPayment", segments: [2] },
  "subscriptions-plans": { key: "SubscriptionDetails", segments: [3] },
};

// Path segments come from the URL, so look them up as own keys only.
const lookup = <T>(table: Record<string, T>, segment = ""): T | undefined =>
  Object.prototype.hasOwnProperty.call(table, segment)
    ? table[segment]
    : undefined;

/**
 * The SEO entry for a URL path, following the routes in App.tsx. Matching
 * ignores case and trailing slashes, as the router does. Anything the router
 * would not render as a page resolves to NotFound.
 */
export const resolveSeoKey = (pathname: string): SeoKey => {
  const segments = pathname.toLowerCase().split("/").filter(Boolean);
  const [first, second] = segments;

  if (!first || (segments.length === 1 && first === "invalid")) {
    return "NotFound";
  }

  const link = lookup(LINK_PAGES, first);
  if (link?.segments.includes(segments.length)) {
    return link.key;
  }
  if (
    first === "payment-methods" &&
    second === "update" &&
    segments.length === 3
  ) {
    return "UpdatePaymentMethod";
  }

  if (segments.length === 1) {
    return "Dashboard";
  }
  return (segments.length === 2 && lookup(CLUSTER_PAGES, second)) || "NotFound";
};
